import HeightOutlinedIcon from "@mui/icons-material/HeightOutlined";
import { Box, Checkbox } from "@mui/material";
import type {
	CellClassParams,
	ColDef,
	ICellRendererParams,
	RowHeightParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import {
	panelGridSx,
	SectionPanel,
	TEXT,
} from "@/controls/section-panel/SectionPanel";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import { COLORS } from "../models/constants";
import type { StandData } from "../models/StandData";

const LOOPER_PROPERTIES = [
	{ key: "scannerSel", label: "Scanner Sel" },
	{ key: "heightRecipe", label: "Height Recipe" },
	{ key: "heightActual", label: "Height Actual" },
] as const;

interface LooperGridRow {
	property: string;
	propertyKey: string;
	[standName: string]: StandData | string;
}

const checkboxSx = { p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } } as const;

const LooperCellRenderer = (params: ICellRendererParams) => {
	const stand = params.value as StandData | undefined;
	if (!stand || typeof stand === "string") return null;

	const propertyKey = params.data?.propertyKey;
	const enableComp = stand.EnableRecipeComparison;

	switch (propertyKey) {
		case "scannerSel":
			return (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
					}}
				>
					<Checkbox
						checked={stand.LooperControlSelected}
						size="small"
						disabled
						sx={checkboxSx}
					/>
				</Box>
			);

		case "heightRecipe":
			return (
				<Box
					sx={{
						textAlign: "center",
						...TEXT.dense,
						fontVariantNumeric: "tabular-nums",
						color: "text.secondary",
					}}
				>
					{stand.LooperHeightRecipe != null
						? stand.LooperHeightRecipe.toFixed(1)
						: ""}
				</Box>
			);

		case "heightActual": {
			const looperHeightInches =
				stand.LooperHeight != null ? stand.LooperHeight / 25.4 : null;
			const mismatch =
				enableComp &&
				looperHeightInches != null &&
				stand.LooperHeightRecipe != null &&
				Math.abs(looperHeightInches - stand.LooperHeightRecipe) > 0.1;

			return (
				<Box
					sx={{
						textAlign: "center",
						...TEXT.dense,
						fontVariantNumeric: "tabular-nums",
						...(mismatch
							? { color: COLORS.outOfRange, fontWeight: "bold" }
							: {}),
					}}
				>
					{looperHeightInches != null ? looperHeightInches.toFixed(1) : ""}
				</Box>
			);
		}

		default:
			return null;
	}
};

const looperCellStyle = (_params: CellClassParams) => {
	return undefined;
};

const getRowHeight = (_params: RowHeightParams) => 26;

interface LooperSectionProps {
	stands: StandData[];
}

const looperDefaultColDef: ColDef = {
	...globalDefaultColDef,
	resizable: false,
};

export const LooperSection = ({ stands }: LooperSectionProps) => {
	const looperStands = useMemo(
		() => stands.filter((s) => s.HasLooperControl),
		[stands],
	);

	const rowData = useMemo<LooperGridRow[]>(() => {
		return LOOPER_PROPERTIES.map((prop) => {
			const row: LooperGridRow = {
				property: prop.label,
				propertyKey: prop.key,
			};
			for (const stand of looperStands) {
				row[stand.Name] = stand;
			}
			return row;
		});
	}, [looperStands]);

	const PROPERTY_COL_WIDTH = 110;

	const looperNamesKey = looperStands.map((s) => s.Name).join(",");
	const columnDefs = useMemo<ColDef[]>(() => {
		const propertyCol: ColDef = {
			field: "property",
			headerName: "",
			pinned: "left",
			cellClass: "stand-property-cell",
			wrapText: false,
			autoHeight: false,
			width: PROPERTY_COL_WIDTH,
			minWidth: PROPERTY_COL_WIDTH,
			maxWidth: PROPERTY_COL_WIDTH,
			suppressMovable: true,
			sortable: false,
			filter: false,
			resizable: false,
			cellStyle: {
				...TEXT.head,
				justifyContent: "flex-start",
				textAlign: "left",
			},
		};

		const standCols: ColDef[] = looperStands.map((s) => ({
			colId: s.Name,
			headerName: s.Name,
			valueGetter: (params) => params.data?.[s.Name],
			flex: 1,
			minWidth: 48,
			sortable: false,
			filter: false,
			resizable: false,
			cellRenderer: LooperCellRenderer,
			cellStyle: looperCellStyle,
		}));

		return [propertyCol, ...standCols];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [looperNamesKey]);

	if (looperStands.length === 0) return null;

	return (
		<SectionPanel
			title="Loopers"
			icon={<HeightOutlinedIcon />}
			flush
			sx={{ height: "100%" }}
		>
			<Box sx={panelGridSx}>
				<AgGridReact
					rowData={rowData}
					columnDefs={columnDefs}
					defaultColDef={looperDefaultColDef}
					domLayout="autoHeight"
					headerHeight={26}
					getRowHeight={getRowHeight}
					getRowId={(params) => params.data.propertyKey}
					suppressCellFocus
					suppressContextMenu
					suppressColumnVirtualisation
					suppressHorizontalScroll
					animateRows={false}
				/>
			</Box>
		</SectionPanel>
	);
};
